import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Person,
  Settings,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useThemeMode } from '../../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { formatPerfil } from '../../../utils/formatters';

interface HeaderProps {
  onMenuClick: () => void;
  drawerWidth: number;
}

export function Header({ onMenuClick, drawerWidth }: HeaderProps) {
  const { usuario, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isDark = mode === 'dark';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    handleClose();
    navigate('/perfil');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        bgcolor: isDark ? '#111827' : '#ffffff',
        borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.10)' : '1px solid #e2e8f0',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          sx={{
            mr: 2,
            display: { sm: 'none' },
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            '&:hover': {
              bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={isDark ? 'Modo claro' : 'Modo escuro'}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              mr: 1,
              color: isDark ? '#fbbf24' : '#475569',
              bgcolor: isDark ? 'rgba(251, 191, 36, 0.1)' : '#f8fafc',
              border: isDark ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid #e2e8f0',
              '&:hover': {
                bgcolor: isDark ? 'rgba(251, 191, 36, 0.2)' : '#f1f5f9',
              },
            }}
          >
            {isDark ? <LightMode /> : <DarkMode />}
          </IconButton>
        </Tooltip>

        {usuario && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: isDark ? 'white' : '#1e293b' }}
              >
                {usuario.nome}
              </Typography>
              <Chip
                label={formatPerfil(usuario.perfil)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#eff6ff',
                  color: '#1d4ed8',
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </Box>

            <IconButton
              size="large"
              onClick={handleMenu}
              sx={{
                p: 0.5,
                border: '2px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  border: '2px solid rgba(37, 99, 235, 0.5)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  background: '#2563eb',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                {usuario.nome.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  bgcolor: isDark ? '#0f0f18' : '#ffffff',
                  border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                  '& .MuiMenuItem-root': {
                    borderRadius: 1,
                    mx: 1,
                    my: 0.5,
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                    },
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: isDark ? 'white' : '#1e293b', fontWeight: 600 }}>
                  {usuario.nome}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#64748b' }}>
                  {usuario.email}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)', my: 1 }} />
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <Person fontSize="small" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#64748b' }} />
                </ListItemIcon>
                <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#1e293b' }}>Meu Perfil</Typography>
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <ListItemIcon>
                  <Settings fontSize="small" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#64748b' }} />
                </ListItemIcon>
                <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#1e293b' }}>Configuracoes</Typography>
              </MenuItem>
              <Divider sx={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)', my: 1 }} />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" sx={{ color: '#ef4444' }} />
                </ListItemIcon>
                <Typography sx={{ color: '#ef4444' }}>Sair</Typography>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Header;
